import { Component, Input } from '@angular/core';

import { PropertyService } from '@services';
import { UserService } from '@services/user/user.service';
import { IProperty, PropertyCategory } from '@services/property/property-model.interfaces';
import { Observation, ObservationHistory } from '@services/observations/observation.interfaces';

import * as moment from 'moment';
import { filter, map, orderBy } from 'lodash';

export interface CollectedPropertyValue {
  propertyID: number;
  property: IProperty;
  propertyName: string;
  value: string;
  displayValue: string;
  time: number;
  timeFormatted: string;
  userID: number;
  userName: string;
  category: string;
  activity: string;
}

@Component({
  selector: 'app-property-values-display',
  templateUrl: './property-values-display.component.html',
  styleUrls: ['./property-values-display.component.scss'],
})
export class PropertyValuesDisplayComponent {
  @Input() observation: Observation;
  @Input() displayState: 'new' | 'fix' = 'new';

  public collectedValues: CollectedPropertyValue[] = [];

  constructor(
    private propertyService: PropertyService,
    private userService: UserService
  ) {}

  ngOnInit() {
    this.loadCollectedValues();
  }

  ngOnChanges() {
    this.loadCollectedValues();
  }

  private loadCollectedValues(): void {
    if (!this.observation?.history) {
      this.collectedValues = [];
      return;
    }

    const propertyEvents = filter(this.observation.history, (item: ObservationHistory) =>
      item.relatedType === 'property' &&
      item.relatedData?.state === this.displayState
    );

    this.collectedValues = orderBy(
      map(propertyEvents, (event: ObservationHistory) => {
        const propertyID = +event.relatedItem;
        const property = this.propertyService.getPropertyById(propertyID);
        const value = event.relatedData?.value;

        return {
          propertyID,
          property,
          propertyName: property ? this.propertyService.getPropertyName(property, true) : `Property ${propertyID}`,
          value,
          displayValue: property ? this.propertyService.displayPropertyValue(property, value) : String(value || ''),
          time: event.relatedData?.time || event.time,
          timeFormatted: moment.unix(event.relatedData?.time || event.time).format('lll'),
          userID: event.userID,
          userName: this.userService.getFullname(event.userID),
          category: event.relatedData?.category || property?.category || '',
          activity: event.relatedData?.activity || ''
        } as CollectedPropertyValue;
      }),
      ['time'],
      ['asc']
    );
  }

  public hasValues(): boolean {
    return this.collectedValues.length > 0;
  }

  public isInServiceProperty(value: CollectedPropertyValue): boolean {
    return value.category === PropertyCategory.InService;
  }

  public getInServiceStatus(value: CollectedPropertyValue): { type: string; title: string } | null {
    if (!this.isInServiceProperty(value)) {
      return null;
    }
    return this.propertyService.getAIStatusByValue(+value.value);
  }
}
